// shop-frontend/src/pages/ShopDashboard.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SalesAnalytics from "../components/SalesAnalytics";
import LowStockAlerts from "../components/LowStockAlerts";
import ShopPerformanceScore from "../components/ShopPerformanceScore";
import {
  getShopAnalyticsSummary,
  getShopPublishReadiness,
  getShopId,
  getShopMe,
  getMyShopProducts,
  shopStripeCreateOnboardingLink,
  shopStripeCreateOrGetAccount,
  shopStripeGetStatus,
  shopSavePaymentSettings,
  getShopPublicUrl,
} from "../utils/api";
import { useShopAuth } from "../context/ShopAuthContext";
import "../styles/shopDashboard.css";

function formatCurrency(value) {
  const number = Number(value || 0);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(number);
}

/* =========================
   SETUP STEPS (no Stripe — moved to payment section)
   ========================= */
function useSetupSteps(shop, shopData, productCount) {
  return useMemo(() => {
    const s = shopData || shop || {};
    const hasProfile = !!(s.shopName || s.name) && !!(s.bio || s.phone || s.website);
    const hasLogo = !!(s.logoUrl || s.logo);
    const hasProducts = productCount > 0;

    const steps = [
      {
        key: "profile",
        title: "Complete your shop profile",
        desc: "Add shop name, bio, phone, and logo",
        done: hasProfile && hasLogo,
        action: "/shop/settings",
        actionLabel: "Go to Settings",
      },
      {
        key: "products",
        title: "Add your first product",
        desc: "List at least one product in your shop",
        done: hasProducts,
        action: "/shop/add-product",
        actionLabel: "Add Product",
      },
      {
        key: "publish",
        title: "Publish your shop",
        desc: "Once everything is ready, your shop goes live",
        done: hasProfile && hasLogo && hasProducts,
        action: null,
        actionLabel: "Ready!",
      },
    ];

    const completedCount = steps.filter((s) => s.done).length;
    const allDone = completedCount === steps.length;
    return { steps, completedCount, allDone };
  }, [shop, shopData, productCount]);
}

export default function ShopDashboard() {
  const nav = useNavigate();
  const { shop } = useShopAuth();
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeError, setStripeError] = useState("");
  const [payoutWarning, setPayoutWarning] = useState(null);
  const [connectState, setConnectState] = useState("onboarding_needed");
  const [publishReadiness, setPublishReadiness] = useState({ canPublish: true, missing: [] });
  const [shopData, setShopData] = useState(null);
  const [productCount, setProductCount] = useState(-1);

  // Shop URL
  const [urlCopied, setUrlCopied] = useState(false);
  const publicUrl = useMemo(() => getShopPublicUrl(shopData || shop), [shopData, shop]);

  // Payment settings
  const [paypalEmail, setPaypalEmail] = useState("");
  const [acceptCash, setAcceptCash] = useState(false);
  const [meetupAddress, setMeetupAddress] = useState("");
  const [psSaving, setPsSaving] = useState(false);
  const [psError, setPsError] = useState("");
  const [psSuccess, setPsSuccess] = useState("");

  const shopId = getShopId();
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  const { steps, completedCount, allDone } = useSetupSteps(shop, shopData, productCount);

  // Load shop + products
  useEffect(() => {
    (async () => {
      try {
        const data = await getShopMe();
        const s = data?.shop || data || null;
        setShopData(s);
        if (s?.paymentSettings) {
          setPaypalEmail(s.paymentSettings.paypalEmail || "");
          setAcceptCash(!!s.paymentSettings.acceptCash);
          setMeetupAddress(s.paymentSettings.meetupAddress || "");
        }
      } catch {}
      try {
        const prodRes = await getMyShopProducts({ limit: 1 });
        const products = prodRes?.products || prodRes?.data || prodRes?.items || (Array.isArray(prodRes) ? prodRes : []);
        const total = Number(prodRes?.total ?? prodRes?.count ?? products.length ?? 0);
        setProductCount(total);
      } catch {
        setProductCount(0);
      }
    })();
  }, []);

  const loadSummary = async () => {
    try {
      setSummaryLoading(true);
      setSummaryError(null);
      const data = await getShopAnalyticsSummary();
      setSummary(data);
    } catch (err) {
      setSummaryError(err?.message || "Failed to load summary");
    } finally {
      setSummaryLoading(false);
    }
  };
  useEffect(() => { loadSummary(); }, []);

  const loadStripeStatus = useCallback(async () => {
    if (!shopId) return;
    try {
      setStripeError("");
      const data = await shopStripeGetStatus(shopId);
      setPayoutWarning(data?.payoutWarning || null);
      setConnectState(String(data?.connectState || "onboarding_needed"));
    } catch (err) {
      if ((err?.status || 0) !== 404) {
        setStripeError(err?.message || "Failed to load Stripe status");
      }
    }
  }, [shopId]);
  useEffect(() => { loadStripeStatus(); }, [loadStripeStatus]);

  useEffect(() => {
    (async () => {
      try {
        const readiness = await getShopPublishReadiness();
        setPublishReadiness({
          canPublish: readiness?.canPublish !== false,
          missing: Array.isArray(readiness?.missing) ? readiness.missing : [],
        });
      } catch {
        setPublishReadiness({ canPublish: true, missing: [] });
      }
    })();
  }, []);

  const connectStripe = async () => {
    if (!shopId) { setStripeError("Missing shop session. Please login again."); return; }
    try {
      setStripeLoading(true);
      setStripeError("");
      const accountRes = await shopStripeCreateOrGetAccount(shopId);
      if (!accountRes?.accountId) throw new Error("Failed to create Stripe account. Make sure your shop profile has a valid country.");
      const linkRes = await shopStripeCreateOnboardingLink(shopId);
      if (!linkRes?.url) throw new Error(linkRes?.message || "Failed to create Stripe onboarding link. Please try again.");
      window.location.href = linkRes.url;
    } catch (e) {
      setStripeError(e?.message || "Failed to connect Stripe");
    } finally {
      setStripeLoading(false);
    }
  };

  const handleSavePaymentSettings = async () => {
    try {
      setPsSaving(true);
      setPsError("");
      setPsSuccess("");
      await shopSavePaymentSettings({
        paypalEmail: paypalEmail.trim(),
        acceptCash,
        meetupAddress: acceptCash ? meetupAddress : "",
      });
      setPsSuccess("Payment settings saved!");
      setTimeout(() => setPsSuccess(""), 4000);
    } catch (e) {
      setPsError(e?.message || "Failed to save");
    } finally {
      setPsSaving(false);
    }
  };

  const summaryCards = useMemo(() => {
    const revenueGrowth = Math.round(Number(summary?.revenueGrowth || 0) * 10) / 10;
    const walletMsc = Number(shop?.wallet?.mscCents || 0) / 100;
    return [
      { title: "Wallet (MSC)", value: `${walletMsc.toFixed(2)} MSC`, meta: "Current shop MSC balance" },
      {
        title: "Today Revenue",
        value: formatCurrency(summary?.todayRevenue),
        meta: revenueGrowth === 0 ? "No change vs yesterday" : `${revenueGrowth > 0 ? "+" : ""}${revenueGrowth}% vs yesterday`,
        tone: revenueGrowth >= 0 ? "text-emerald-600" : "text-red-600",
      },
      { title: "Orders Today", value: summary?.todayOrders ?? 0, meta: "Paid, shipped, or completed" },
      { title: "To Ship", value: summary?.pendingOrders ?? 0, meta: "Paid, not shipped yet", tone: (summary?.pendingOrders || 0) > 0 ? "text-orange-600" : "text-emerald-600" },
      { title: "Low Stock Products", value: summary?.lowStockProducts ?? 0, meta: "Stock threshold alerts", tone: (summary?.lowStockProducts || 0) > 0 ? "text-orange-600" : "text-emerald-600" },
    ];
  }, [summary, shop?.wallet?.mscCents]);

  return (
    <div className="sd-responsive-wrap space-y-6 max-w-[1400px] mx-auto shop-dashboard sd-page min-h-screen">

      {/* ═══ HEADER ═══ */}
      <div className="sd-card sd-header rounded-xl sd-header-pad">
        <h1 className="sd-title sd-title-responsive font-bold mb-2">Home</h1>
        <p className="sd-subtitle text-base">{greeting}, here's what's happening in your shop today.</p>
        <div className="mt-3 inline-flex rounded-lg border border-gray-200 px-3 py-1 text-sm font-semibold text-gray-700">
          Wallet: {(Number(shop?.wallet?.mscCents || 0) / 100).toFixed(2)} MSC
        </div>
      </div>

      {/* ═══ YOUR MOONDALA LINK ═══ */}
      {publicUrl && (
        <div className="sd-shopurl-card">
          <div className="sd-shopurl-left">
            <span className="sd-shopurl-icon">🔗</span>
            <div>
              <div className="sd-shopurl-label">Your Moondala Link</div>
              <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="sd-shopurl-url">
                {publicUrl}
              </a>
            </div>
          </div>
          <div className="sd-shopurl-actions">
            <button
              className={`sd-shopurl-btn ${urlCopied ? "sd-shopurl-copied" : ""}`}
              onClick={() => { navigator.clipboard.writeText(publicUrl); setUrlCopied(true); setTimeout(() => setUrlCopied(false), 2500); }}
            >
              {urlCopied ? "✓ Copied!" : "📋 Copy Link"}
            </button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent("Check out my shop on Moondala: " + publicUrl)}`}
              target="_blank" rel="noopener noreferrer"
              className="sd-shopurl-share"
            >
              Share
            </a>
          </div>
        </div>
      )}

      {/* ═══ SETUP STEPS ═══ */}
      {!allDone && (
        <div className="sd-setup-card">
          <div className="sd-setup-header">
            <div>
              <h2 className="sd-setup-title">🚀 Get your shop ready</h2>
              <p className="sd-setup-sub">{completedCount} of {steps.length} steps completed</p>
            </div>
            <div className="sd-setup-progress-ring">
              <span className="sd-setup-progress-text">{Math.round((completedCount / steps.length) * 100)}%</span>
            </div>
          </div>
          <div className="sd-setup-bar-bg">
            <div className="sd-setup-bar-fill" style={{ width: `${(completedCount / steps.length) * 100}%` }} />
          </div>
          <div className="sd-setup-steps">
            {steps.map((step, i) => (
              <div key={step.key} className={`sd-setup-step ${step.done ? "sd-step-done" : ""}`}>
                <div className="sd-step-num">{step.done ? "✓" : i + 1}</div>
                <div className="sd-step-info">
                  <div className="sd-step-title">{step.title}</div>
                  <div className="sd-step-desc">{step.desc}</div>
                </div>
                {!step.done && step.action && (
                  <button className="sd-step-btn" onClick={() => nav(step.action)}>
                    {step.actionLabel}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══ PUBLISH READINESS ═══ */}
      {!publishReadiness.canPublish && (
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 shadow-sm">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-amber-900 font-semibold">Shipping address incomplete — products can't be published until fixed.</div>
              <div className="text-amber-700 text-sm mt-1">Missing: {publishReadiness.missing.join(", ")}</div>
            </div>
            <button onClick={() => nav("/shop/settings")} className="px-3 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 font-medium">
              Open Shop Settings
            </button>
          </div>
        </div>
      )}

      {/* ═══ SUMMARY CARDS ═══ */}
      {summaryError ? (
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-red-600">{summaryError}</p>
            <button onClick={loadSummary} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg transition-colors font-medium">Retry</button>
          </div>
        </div>
      ) : (
        <div className="sd-summary-grid">
          {summaryLoading
            ? Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="sd-summary-card animate-pulse">
                  <div className="h-4 w-24 bg-gray-200 rounded mb-3" />
                  <div className="h-8 w-20 bg-gray-200 rounded mb-2" />
                  <div className="h-3 w-32 bg-gray-200 rounded" />
                </div>
              ))
            : summaryCards.map((card) => (
                <div key={card.title} className="sd-summary-card">
                  <div className="sd-summary-label">{card.title}</div>
                  <div className={`sd-summary-value ${card.tone || ""}`}>{card.value}</div>
                  <div className="sd-summary-meta">{card.meta}</div>
                </div>
              ))}
        </div>
      )}

      {/* ═══ 💰 HOW DO YOU WANT TO GET PAID? ═══ */}
      <div className="sd-ps-card">
        <div className="sd-ps-header">
          <div>
            <h2 className="sd-ps-title">💰 How do you want to get paid?</h2>
            <p className="sd-ps-sub">Choose how Moondala sends you your earnings.</p>
          </div>
        </div>

        <div className="sd-ps-grid">
          {/* ── Card 1: Stripe Payouts ── */}
          <div className="sd-ps-option">
            <div className="sd-ps-option-icon">💳</div>
            <div className="sd-ps-option-body">
              <div className="sd-ps-option-title">Stripe Payouts</div>
              <div className="sd-ps-option-desc">
                Connect Stripe Express to receive direct payouts from online orders.
              </div>
              {connectState === "connected" ? (
                <div className="sd-ps-stripe-connected">✅ Stripe connected — payouts enabled</div>
              ) : (
                <>
                  {connectState === "pending" && (
                    <div className="sd-ps-stripe-pending">⏳ Account under review — payouts coming soon</div>
                  )}
                  <button className="sd-ps-stripe-btn" onClick={connectStripe} disabled={stripeLoading}>
                    {stripeLoading ? "Connecting..." : "Connect Stripe"}
                  </button>
                  {stripeError && <div className="sd-ps-error" style={{ marginTop: 8 }}>{stripeError}</div>}
                </>
              )}
              {payoutWarning && <div className="sd-ps-stripe-pending" style={{ marginTop: 6 }}>{payoutWarning}</div>}
            </div>
          </div>

          {/* ── Card 2: PayPal Payouts ── */}
          <div className="sd-ps-option">
            <div className="sd-ps-option-icon">🅿️</div>
            <div className="sd-ps-option-body">
              <div className="sd-ps-option-title">PayPal Payouts</div>
              <div className="sd-ps-option-desc">
                Get your earnings sent to your PayPal account — ideal where Stripe isn't available.
                Enter the email on your PayPal account.
              </div>
              <input
                className="sd-ps-input"
                type="email"
                placeholder="your-paypal@email.com"
                value={paypalEmail}
                onChange={(e) => setPaypalEmail(e.target.value)}
              />
              {paypalEmail.trim() && (
                <div className="sd-ps-stripe-connected" style={{ marginTop: 8 }}>
                  ✅ Payouts will be sent to {paypalEmail.trim()}
                </div>
              )}
            </div>
          </div>

          {/* ── Card 3: Cash ── */}
          <div className="sd-ps-option">
            <div className="sd-ps-option-icon">💵</div>
            <div className="sd-ps-option-body">
              <div className="sd-ps-option-header">
                <div>
                  <div className="sd-ps-option-title">Cash Payment</div>
                  <div className="sd-ps-option-desc">Customers pay you in person. You arrange the meetup.</div>
                </div>
                <label className="sd-ps-toggle">
                  <input type="checkbox" checked={acceptCash} onChange={(e) => setAcceptCash(e.target.checked)} />
                  <span className="sd-ps-toggle-slider" />
                </label>
              </div>
              {acceptCash && (
                <div className="sd-ps-meetup-wrap">
                  <label className="sd-ps-label">📍 Meetup Address</label>
                  <textarea
                    className="sd-ps-textarea"
                    rows={3}
                    placeholder={`e.g. Downtown Coffee Shop, 123 Main St\nAvailable Mon–Sat 10am–6pm`}
                    value={meetupAddress}
                    onChange={(e) => setMeetupAddress(e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {psError && <div className="sd-ps-error">{psError}</div>}
        {psSuccess && <div className="sd-ps-success">✓ {psSuccess}</div>}

        <button className="sd-ps-save-btn" onClick={handleSavePaymentSettings} disabled={psSaving}>
          {psSaving ? "Saving..." : "Save Payment Settings"}
        </button>
      </div>

      {/* ═══ PERFORMANCE + STOCK ═══ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ShopPerformanceScore compact={true} />
        <LowStockAlerts compact={true} />
      </div>

      {/* ═══ SALES ANALYTICS ═══ */}
      <SalesAnalytics />
    </div>
  );
}
